import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Modal, Fade, Typography, Box, Backdrop, Button, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { LoadingButton } from '@mui/lab';
import { useDispatch } from 'react-redux';
import InputField from '../../../../components/formElements/InputField';
import CategoryAutocomplete from '../CategoryAutocomplete';
import { useCreateItemCategorymasterMutation, useListItemCategorymasterQuery } from '../../../../redux/apis/itemCategory.api';
import { dashboardApi } from '../../../../redux/apis/dashboardApi';
import { showErrorSnackbar, showSuccessSnackbar, showWarningSnackbar } from '../../../../components/snackBar/snakbar';
import type { CreateItemCategorymasterMutation, CreateItemCategorymasterMutationVariables } from '../../../../graphql/API';

const style = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  bgcolor: 'background.paper',
  boxShadow: 24,
  outline: 'none',
  '@media (min-width:600px)': {
    width: 'auto',
  },
  width: '90%',
};

interface FormState {
  parent: string | number;
  category: string;
  errorStatus: {
    parent: boolean;
    category: boolean;
  };
  errorFields: {
    parent: string;
    category: string;
  };
}

interface ParentData {
  name?: string;
  category?: string;
  type?: string;
  id?: number;
  parent?: number;
  todo?: number;
  relatedData?: {
    ItemCategoryID?: number;
    CompanyID?: number;
    EnteredTimeStamp?: number;
    ExpiredTimeStamp?: number;
    Category?: string;
    ItemCategoryGroupID?: number;
    MainCategory?: string;
  };
}

interface EditDetails {
  child?: {
    category?: string;
    itemcategorygroupid?: number;
    itemcategoryid?: number;
  };
  parent?: {
    Category?: string;
    maincategory?: string;
    itemcategoryid?: number;
    itemcategorygroupid?: number;
    companyid?: number;
    enteredtimestamp?: number;
    expiredtimestamp?: number;
  };
}

interface IDParent {
  id?: number;
}

interface CategoryTreeNode {
  checked?: boolean;
  children?: CategoryTreeNode[];
  [key: string]: unknown;
}

interface AddCategoryModalProps {
  open: boolean;
  MainParent?: string;
  setCheckedLabel?: (id: number) => void;
  setStateS?: ((prev: { itemCtegorey?: number }) => { itemCtegorey?: number }) | ((ids: number[]) => void);
  params?: {
    row?: {
      ItemType?: string;
    };
  };
  setOpen: (open: boolean) => void;
  addparents?: Array<{ value: string | number; label: string }>;
  dataArr?: CategoryTreeNode[];
  setCategoryData?: (data: CreateItemCategorymasterMutation['createItemCategorymaster']) => void;
  setCloseEvent?: (close: boolean) => void;
  listCategory?: () => void | Promise<void>;
  FnAftrClose?: () => void;
  IDparent?: IDParent;
  editDetails?: EditDetails;
}

const AddCategoryModal: React.FC<AddCategoryModalProps> = ({
  open,
  MainParent,
  setCheckedLabel,
  setStateS,
  params,
  setOpen,
  addparents,
  dataArr,
  setCategoryData,
  setCloseEvent,
  listCategory,
  FnAftrClose,
  IDparent,
  editDetails,
}) => {
  const dispatch = useDispatch();
  const [parentId, setPrentsId] = useState<number | undefined>();
  const [loading, setLoading] = useState(false);
  const [state, setState] = useState<FormState>({
    parent: '',
    category: '',
    errorStatus: {
      parent: false,
      category: false,
    },
    errorFields: {
      parent: '',
      category: '',
    },
  });

  const [createCategory] = useCreateItemCategorymasterMutation();

  // Get the main category (Goods or Services) name
  const getMainCategoryName = useCallback(() => {
    if (MainParent !== undefined) {
      return MainParent.toLowerCase() === 'goods' ? 'Goods' : 'Services';
    } else {
      return params?.row?.ItemType?.toLowerCase() === 'goods' ? 'Goods' : 'Services';
    }
  }, [MainParent, params]);

  const mainCategoryName = getMainCategoryName();
  const { data: categoryListData } = useListItemCategorymasterQuery({ maincategory: mainCategoryName });

  // Find the Goods or Services parent category ID (the one with itemcategorygroupid === 0)
  const mainCategoryId = useMemo(() => {
    if (!categoryListData?.listItemCategorymaster) return undefined;
    const mainCategory = categoryListData.listItemCategorymaster.find(
      (item) => item?.category === mainCategoryName && (item?.itemcategorygroupid === 0 || item?.itemcategorygroupid === null)
    );
    return mainCategory?.itemcategoryid ?? undefined;
  }, [categoryListData, mainCategoryName]);

  useEffect(() => {
    if (IDparent?.id) {
      setState((prev) => ({
        ...prev,
        parent: IDparent.id!,
      }));
    }
    if (editDetails) {
      setState((prev) => ({
        ...prev,
        category: editDetails?.child?.category || '',
        parent: editDetails?.child?.itemcategorygroupid || '',
      }));
    }
  }, [IDparent, editDetails]);

  const findCheckedValue = useCallback((item: CategoryTreeNode) => {
    if (item?.checked === true) {
      item.checked = false;
    } else if (item?.children && item.children.length > 0) {
      for (let i = 0; i < item.children.length; i++) {
        findCheckedValue(item.children[i]);
      }
    }
  }, []);

  useEffect(() => {
    if (dataArr) {
      dataArr.forEach((item) => {
        findCheckedValue(item);
      });
    }
  }, [dataArr, findCheckedValue]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.name as keyof FormState;
    let value: string | number = e.target.value;

    // Capitalize the first letter of each word for category field
    // Rest of each word stays as the user types
    if (name === 'category' && typeof value === 'string' && value.length > 0) {
      value = value
        .split(' ')
        .map((word) => (word.length > 0 ? word.charAt(0).toUpperCase() + word.slice(1) : ''))
        .join(' ');
    }

    if (value === '') {
      setState((prev) => ({
        ...prev,
        [name]: value,
        errorFields: {
          ...prev.errorFields,
          [name]: 'Category is required',
        },
        errorStatus: {
          ...prev.errorStatus,
          [name]: true,
        },
      }));
    } else {
      setState((prev) => ({
        ...prev,
        [name]: value,
        errorFields: {
          ...prev.errorFields,
          [name]: '',
        },
        errorStatus: {
          ...prev.errorStatus,
          [name]: false,
        },
      }));
    }
  }, []);

  const createnewCategory = useCallback(
    async (data: CreateItemCategorymasterMutationVariables['createItemCategorymasterInput']) => {
      try {
        let res: {
          data: {
            updateItemCategorymaster?: CreateItemCategorymasterMutation['createItemCategorymaster'];
            createItemCategorymaster?: CreateItemCategorymasterMutation['createItemCategorymaster'];
          };
        };

        if (editDetails) {
          const editResult = await createCategory({
            createItemCategorymasterInput: {
              ...data,
              itemcategoryid: editDetails?.child?.itemcategoryid ?? 0,
            },
          });

          if ('error' in editResult) {
            throw new Error('Failed to update category');
          }

          res = {
            data: {
              updateItemCategorymaster: editResult.data?.createItemCategorymaster ?? null,
            },
          };
        } else {
          const createResult = await createCategory({ createItemCategorymasterInput: data });

          if ('error' in createResult) {
            throw new Error('Failed to create category');
          }

          res = {
            data: {
              createItemCategorymaster: createResult.data?.createItemCategorymaster ?? null,
            },
          };
        }

        const responseData = editDetails ? res.data.updateItemCategorymaster : res.data.createItemCategorymaster;

        if (responseData?.statuscode === 'Error') {
          setLoading(false);
          showWarningSnackbar(responseData.statusmessage || 'An error occurred');
        } else {
          if (editDetails) {
            if (res?.data?.updateItemCategorymaster) {
              dispatch(dashboardApi.util.invalidateTags(['Dashboard_itemCategoryMaster']));
              setOpen(false);
              showSuccessSnackbar('Category Updated Successfully');
              if (listCategory) {
                listCategory();
              }
            }
          } else if (
            res?.data?.createItemCategorymaster?.itemcategoryid !== null &&
            res?.data?.createItemCategorymaster?.itemcategoryid !== undefined
          ) {
            const categoryData = res.data.createItemCategorymaster;

            // Helper function to update form field and callbacks
            const updateFormField = () => {
              if (setCategoryData) {
                setCategoryData(categoryData);
                if (setCloseEvent) {
                  setCloseEvent(false);
                }
              }

              if (setCheckedLabel && categoryData?.itemcategoryid) {
                setCheckedLabel(categoryData.itemcategoryid);
              }

              if (setStateS) {
                if (IDparent) {
                  if (typeof setStateS === 'function' && categoryData?.itemcategoryid) {
                    (setStateS as (ids: number[]) => void)([categoryData.itemcategoryid]);
                  }
                } else {
                  if (typeof setStateS === 'function' && categoryData?.itemcategoryid) {
                    (setStateS as (prev: { itemCtegorey?: number }) => { itemCtegorey?: number })({
                      itemCtegorey: categoryData.itemcategoryid,
                    });
                  }
                }
              }
            };

            // First, refetch the category list to ensure the new category is available
            if (listCategory) {
              try {
                const refetchResult = listCategory();
                // Check if it returns a Promise
                if (refetchResult && typeof refetchResult === 'object' && 'then' in refetchResult) {
                  // It's a Promise, wait for it to complete
                  (refetchResult as Promise<void>)
                    .then(() => {
                      updateFormField();
                    })
                    .catch((error: unknown) => {
                      console.error('Error refetching categories:', error);
                      // Even if refetch fails, still update the form field
                      updateFormField();
                    });
                } else {
                  // It's not a Promise, update immediately
                  updateFormField();
                }
              } catch (error: unknown) {
                console.error('Error calling listCategory:', error);
                // Even if call fails, still update the form field
                updateFormField();
              }
            } else {
              // If listCategory is not provided, update immediately
              updateFormField();
            }

            setOpen(false);
            showSuccessSnackbar('Category Created Successfully');
            dispatch(dashboardApi.util.invalidateTags(['Dashboard_itemCategoryMaster']));
            setState((prev) => ({
              ...prev,
              parent: '',
              category: '',
            }));
            setLoading(false);
          } else {
            setLoading(false);
            showErrorSnackbar('Something went wrong! Please try again');
          }
        }
      } catch (error) {
        setLoading(false);
        console.log('error', error);
        showErrorSnackbar('An error occurred while saving the category');
      }
    },
    [editDetails, createCategory, dispatch, setOpen, listCategory, setCategoryData, setCloseEvent, setCheckedLabel, setStateS, IDparent]
  );

 

  const formValidation = useCallback(
    (data: { parent: string | number; category: string }) => {
      let isvalid = true;
      const obj: FormState = { ...state };

      Object.keys(data).forEach((itemKey) => {
        if (itemKey === 'category') {
          const categoryValue = data[itemKey as keyof typeof data];
          if (typeof categoryValue === 'string' && categoryValue.trim().length <= 0) {
            isvalid = false;
            obj.errorFields.category = 'Category is required';
            obj.errorStatus.category = true;
          } else {
            obj.errorFields.category = '';
            obj.errorStatus.category = false;
          }
        }
      });

      setState(obj);
      return isvalid;
    },
    [state]
  );

  const addNewCategory = useCallback(() => {
    const { parent, category } = state;
    const formData = {
      parent,
      category: typeof category === 'string' ? category : '',
    };

    if (formValidation(formData)) {
      setLoading(true);
      let mainCategory: string;
      if (MainParent !== undefined) {
        mainCategory = MainParent.toLowerCase() === 'goods' ? 'Goods' : 'Services';
      } else {
        mainCategory = params?.row?.ItemType?.toLowerCase() === 'goods' ? 'Goods' : 'Services';
      }

      // If no parent is selected, default to Goods or Services parent category
      // Only Goods and Services should have itemcategorygroupid === 0
      let finalParentId: number;
      if (parent === '') {
        // Use mainCategoryId (Goods/Services) if available, otherwise use parentId, fallback to 0
        finalParentId = mainCategoryId ?? parentId ?? 0;
      } else {
        finalParentId = typeof parent === 'number' ? parent : Number(parent) || 0;
      }

      const categoryData: CreateItemCategorymasterMutationVariables['createItemCategorymasterInput'] = {
        maincategory: mainCategory,
        category: category, // Save exactly as user typed (allows any case including full uppercase)
        itemcategorygroupid: finalParentId,
        itemcategoryid: 0,
        expiredtimestamp: 0,
      };

      createnewCategory(categoryData);
    }
  }, [state, formValidation, MainParent, params, parentId, mainCategoryId, createnewCategory]);

   useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.key.toLowerCase() === 's') {
        e.preventDefault();
        addNewCategory();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [state]);

  const handleClose = useCallback(
    (event: object, reason?: string) => {
      if (reason !== 'backdropClick') {
        setOpen(false);
        setState((prev) => ({
          ...prev,
          parent: '',
          category: '',
          errorStatus: {
            ...prev.errorStatus,
            parent: false,
            category: false,
          },
          errorFields: {
            ...prev.errorFields,
            parent: '',
            category: '',
          },
        }));
      }
    },
    [setOpen]
  );

  const categoryChange = useCallback((categoryId: number | null) => {
    setState((prev) => ({
      ...prev,
      parent: categoryId ?? '',
    }));
  }, []);

  useEffect(() => {
    const element = document?.getElementById('category') as HTMLInputElement | null;
    element?.focus();
  }, []);

  return (
    <div
      id={open ? 'addCategory' : ''}
      onKeyDown={(e: React.KeyboardEvent) => {
        if (e.code === 'Enter' || e.code === 'NumpadEnter') {
          addNewCategory();
        }
        if (e.key === 'Tab') {
          e.preventDefault();
          e.stopPropagation();
        }
      }}
    >
      <Modal
        aria-labelledby="transition-modal-title"
        aria-describedby="transition-modal-description"
        open={open}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
        onClose={handleClose}
      >
        <Fade in={open}>
          <Box sx={style}>
            <Box
              sx={{
                backgroundColor: 'primary.main',
                padding: '5px 15px',
                display: 'flex',
                flexDirection: 'row',
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography
                  sx={{
                    color: '#fff',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  {editDetails ? 'Edit New Category' : 'Add New Category'}

                  <IconButton
                    aria-label="delete"
                    onClick={() => {
                      if (!loading) {
                        handleClose({}, 'closeClick');
                      }
                    }}
                  >
                    <CloseIcon sx={{ color: '#fff' }} />
                  </IconButton>
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                padding: { xs: '5px', sm: '15px' },
              }}
            >
              <Box sx={{ display: 'flex' }}>
                <Box sx={{ flex: { xs: 1, sm: 0.5 }, m: 1 }}>
                  <InputField
                    name="category"
                    id="category"
                    placeholder={editDetails ? 'Category' : 'New Category'}
                    size="small"
                    required={true}
                    type="text"
                    state={state}
                    value={state.category}
                    onChange={handleChange as any}
                    onBlur={() => {
                      setState((prev) => ({
                        ...prev,
                        category: prev.category.replace(/\s+/g, ' ').trim(),
                      }));
                    }}
                    disable={false}
                    endAdornment={undefined}
                    style={undefined}
                    inputRef={undefined}
                    onKeyDown={undefined}
                    autoFocus={false}
                    from={undefined}
                    maxLength={50}
                    InputProps={undefined}
                    onFocus={undefined}
                  />
                </Box>
                <Box sx={{ flex: { xs: 1, sm: 0.5 }, m: 1 }}>
                  <CategoryAutocomplete
                    type={
                      MainParent === 'GOODS' || MainParent === 'Goods' || (MainParent !== undefined && MainParent.toLowerCase() === 'goods')
                        ? 'GOODS'
                        : 'SERVICE'
                    }
                    value={
                      typeof state.parent === 'number'
                        ? state.parent
                        : typeof state.parent === 'string' && state.parent !== ''
                        ? Number(state.parent) || null
                        : null
                    }
                    onChange={categoryChange}
                    disabled={false}
                    setup={'NotNeed'}
                  />
                </Box>
              </Box>
            </Box>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
              }}
            >
              <Button
                disabled={loading}
                onClick={() => handleClose({}, 'closeClick')}
                variant="outlined"
                sx={{ flex: 1, margin: '10px', fontSize: '12px' }}
              >
                Cancel
              </Button>
              <LoadingButton
                sx={{ flex: 1, margin: '10px', fontSize: '12px' }}
                loading={loading}
                disableRipple={true}
                variant="contained"
                onClick={() => addNewCategory()}
              >
                {editDetails ? 'Update' : 'Add'}
              </LoadingButton>
            </Box>
          </Box>
        </Fade>
      </Modal>
    </div>
  );
};

export default AddCategoryModal;
